import ApiService from "@/core/services/ApiService";
import { Actions, Mutations } from "@/store/enums/StoreEnums";
import { Module, Action, Mutation, VuexModule } from "vuex-module-decorators";

// 抽赏优惠券接口
export interface LotteryCoupon {
  id: number;
  name: string;
  coupon_type: string;
  coupon_type_text: string;
  discount_value: number;
  discount_text: string;
  min_amount: number;
  max_discount: number | null;
  box_info_ids: number[];
  box_info_text: string;
  allowed_times: number[];
  times_text: string;
  total_quantity: number | null;
  claimed_quantity: number;
  per_user_limit: number | null;
  start_time: string | null;
  end_time: string | null;
  validity_days: number | null;
  status: string;
  allow_user_claim: boolean;
  created_at: string;
  updated_at: string;
  stats?: {
    total_claimed: number;
    total_used: number;
    total_expired: number;
    total_unused: number;
  };
}

// 用户持有抽赏优惠券接口
export interface UserLotteryCouponItem {
  id: number;
  user_id: number;
  user_nickname: string;
  user_mobile: string;
  status: string;
  status_text: string;
  expire_at: string | null;
  used_at: string | null;
  payment_record_id: number | null;
  created_at: string;
}

// 抽赏优惠券使用情况接口
export interface LotteryCouponUsage {
  user_lottery_coupons: Array<UserLotteryCouponItem>;
  total: number;
  stats: {
    total_claimed: number;
    total_used: number;
    total_expired: number;
    total_unused: number;
  };
}

// 套系选项接口
export interface BoxInfoOption {
  id: number;
  name: string;
}

@Module
export default class LotteryCouponModule extends VuexModule {
  lottery_coupons = [] as Array<LotteryCoupon>;
  lottery_coupon_count = 0;
  lottery_coupon = {} as LotteryCoupon;
  lottery_coupon_usage = {} as LotteryCouponUsage;
  lottery_coupon_box_infos = [] as Array<BoxInfoOption>;

  get currentLotteryCoupon(): LotteryCoupon {
    return this.lottery_coupon;
  }

  get currentLotteryCoupons(): Array<LotteryCoupon> {
    return this.lottery_coupons;
  }

  get currentLotteryCouponCount() {
    return this.lottery_coupon_count;
  }

  get currentLotteryCouponUsage(): LotteryCouponUsage {
    return this.lottery_coupon_usage;
  }

  get currentLotteryCouponBoxInfos(): Array<BoxInfoOption> {
    return this.lottery_coupon_box_infos;
  }

  @Mutation
  [Mutations.SET_LOTTERY_COUPONS](lottery_coupons) {
    this.lottery_coupons = lottery_coupons;
  }

  @Mutation
  [Mutations.SET_LOTTERY_COUPON](lottery_coupon) {
    this.lottery_coupon = lottery_coupon;
  }

  @Mutation
  [Mutations.SET_LOTTERY_COUPON_COUNT](lottery_coupon_count) {
    this.lottery_coupon_count = lottery_coupon_count;
  }

  @Mutation
  [Mutations.SET_LOTTERY_COUPON_USAGE](lottery_coupon_usage) {
    this.lottery_coupon_usage = lottery_coupon_usage;
  }

  @Mutation
  [Mutations.SET_LOTTERY_COUPON_BOX_INFOS](box_infos) {
    this.lottery_coupon_box_infos = box_infos;
  }

  @Action
  [Actions.GET_LOTTERY_COUPONS](params) {
    ApiService.setHeader();

    let queryString = "?";

    if (params) {
      Object.keys(params).forEach((i) => {
        if (params[i] !== undefined && params[i] !== null && params[i] !== '') {
          queryString += "&" + i + "=" + params[i];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/lottery_coupons", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_LOTTERY_COUPONS, data["lottery_coupons"]);
          this.context.commit(Mutations.SET_LOTTERY_COUPON_COUNT, data["total"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_LOTTERY_COUPON](id) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/lottery_coupons/${id}`)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_LOTTERY_COUPON, data["lottery_coupon"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.CREATE_LOTTERY_COUPON](params) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.post("admin/lottery_coupons", params)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_LOTTERY_COUPON, data["lottery_coupon"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.UPDATE_LOTTERY_COUPON](credentials) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.put(`admin/lottery_coupons/${credentials.id}`, credentials.values)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_LOTTERY_COUPON, data["lottery_coupon"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.DELETE_LOTTERY_COUPON](id) {
    ApiService.setHeader();

    return new Promise<void>((resolve, reject) => {
      ApiService.delete(`admin/lottery_coupons/${id}`)
        .then(() => {
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GRANT_LOTTERY_COUPON](params) {
    ApiService.setHeader();

    // 支持 user_ids 或 mobiles 参数
    const requestParams: { user_ids?: number[]; mobiles?: string[] } = {};
    if (params.user_ids && params.user_ids.length > 0) {
      requestParams.user_ids = params.user_ids;
    }
    if (params.mobiles && params.mobiles.length > 0) {
      requestParams.mobiles = params.mobiles;
    }

    return new Promise<any>((resolve, reject) => {
      ApiService.post(`admin/lottery_coupons/${params.id}/grant`, requestParams)
        .then(({ data }) => {
          resolve(data);
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_LOTTERY_COUPON_USAGE](params) {
    ApiService.setHeader();

    let queryString = "?";

    if (params) {
      Object.keys(params).forEach((i) => {
        if (i !== 'id' && params[i] !== undefined && params[i] !== null && params[i] !== '') {
          queryString += "&" + i + "=" + params[i];
        }
      });
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get(`admin/lottery_coupons/${params.id}/usage`, queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_LOTTERY_COUPON_USAGE, {
            user_lottery_coupons: data["user_lottery_coupons"],
            total: data["total"],
            stats: data["stats"]
          });
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }

  @Action
  [Actions.GET_LOTTERY_COUPON_BOX_INFOS](params?: { name?: string }) {
    ApiService.setHeader();

    let queryString = "";
    if (params?.name) {
      queryString = `?name=${encodeURIComponent(params.name)}`;
    }

    return new Promise<void>((resolve, reject) => {
      ApiService.get("admin/lottery_coupons/box_infos", queryString)
        .then(({ data }) => {
          this.context.commit(Mutations.SET_LOTTERY_COUPON_BOX_INFOS, data["box_infos"]);
          resolve();
        })
        .catch(({ response }) => {
          this.context.commit(Mutations.SET_ERROR, response?.data?.errors || '请求失败');
          reject();
        });
    });
  }
}
